import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const ZoneForm = NiceModal.create(({ id, mode, modalName }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup.object().shape({
				warehouseID: yup.number().nullable().required(),
				name: yup.string().nullable().required(),
				pickerID: yup
					.number()
					.nullable()
					.when('defaultPicker', {
						is: (val) => val === true || val === 1,
						then: (schema) => schema.required()
					}),
				defaultPicker: yup.boolean().nullable().required()
			}),
		[]
	);
	const [loading, setLoading] = useState(false);
	const isAuthorized = useAccessControl();

	const viewMode = mode === 'view';

	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const { showSuccess, showError } = useAlert();

	// getters
	const getTitle = () => (mode === 'edit' ? 'editZone' : mode === 'view' ? 'viewZone' : 'addZone');
	const getSubmitLabel = () => (mode === 'edit' ? 'save' : mode === 'view' ? 'button.view' : 'button.add');

	const handleCloseModal = () => closeModal();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			zonePickers: [
				{
					zonePickerID: null,
					default: null
				}
			]
		}
	});
	const {
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);
	const defaultColSpan = {
		md: 6
	};
	const onSubmit = async (data) => {
		setLoading(true);

		const URL = id ? ADVANCE_WMS_MODULE.UPDATE_ZONE : ADVANCE_WMS_MODULE.ADD_ZONE;

		const body = id ? { ...data, ID: parseInt(id) } : { ...data };
		try {
			const { status } = await axios.post(URL, body);
			if (status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.ZONES));
				closeModal();
			}
		} catch (error) {
			if (error.response?.data?.code === 'error.F010') {
				showError({
					message: 'error.F010'
				});
			}
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};
	const fillForm = async () => {
		setLoading(true);
		try {
			const { data } = await axios.post(ADVANCE_WMS_MODULE.GET_ZONE_BY_ID, { id: parseInt(id) });
			reset({ ...data });
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		id && fillForm();
	}, [id]);

	const warehouseAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const pickerAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<Modal
			name={modalName}
			maxWidth="small"
			fullwidth
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={getTitle()}
					tooltip={['edit', 'view'].includes(mode) ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{getTitle()}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="warehouseName" isLoading={loading} required>
						<AutoComplete
							name="warehouseID"
							disabled={viewMode}
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							setValue={(value) => {
								setValue('warehouseID', value?.ID, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('warehouseID')}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							firstItem={
								isAuthorized('/autocomplete/product/addANewWarehouse')
									? {
											action: () =>
												NiceModal.show(WarehouseFormShort, {
													setWarehouse: (value) => {
														setValue(`warehouseID`, value, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewWarehouse</Typography>
										}
									: undefined
							}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="zoneName" isLoading={loading} required>
						<StringInput disabled={viewMode} name="name" />
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="picker"
						isLoading={loading}
						required={watch('defaultPicker') === true || watch('defaultPicker') === 1}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.OTO_PACK_USERS}
							name={`pickerID`}
							value={watch(`pickerID`)}
							setValue={(value) => {
								setValue(`pickerID`, value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							columns={pickerAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							disabled={viewMode}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="default" isLoading={loading}>
						<SwitchInput name={`defaultPicker`} className="mt-3" disabled={viewMode} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{!viewMode && (
					<Button
						label={getSubmitLabel()}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						loading={loading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});
export default ZoneForm;
